import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOSchema from '@/components/feature/SEOSchema';
import { getArticleBySlug, getRelatedArticles, articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-3xl font-black text-foreground-950">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 font-semibold">返回首页</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  return (
    <>
      <SEOSchema
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '911爆料网',
          },
          url: `https://911baoliao.com/article/${article.slug}`,
        }}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${article.title} - 911爆料网`,
          url: `https://911baoliao.com/article/${article.slug}`,
          description: article.excerpt,
        }}
      />

      <article>
        <section className="pt-24 pb-12 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-4xl mx-auto">
              <div className="mb-5">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: article.category, href: `/category/${article.categorySlug}` },
                    { label: article.title },
                  ]}
                />
              </div>
            </div>

            <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-10">
              <div className="lg:w-1/2">
                <div className="rounded-[30px] overflow-hidden h-[320px] md:h-[420px]">
                  <img
                    src={article.image}
                    alt={article.imageAlt}
                    title={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:w-1/2 flex flex-col justify-center">
                <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent-100 text-accent-700 rounded-full mb-4 w-fit">
                  {article.category}
                </span>

                <h1 className="text-2xl md:text-4xl font-black text-foreground-950 leading-[1.15] tracking-tight">
                  {article.title}
                </h1>

                <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>

                <div className="mt-6 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground-900">{article.author}</p>
                      <p className="text-[11px] text-foreground-500">{article.authorTitle}</p>
                    </div>
                  </div>
                  <div className="h-8 w-px bg-background-200"></div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-calendar-line"></i>
                    <span>{article.date}</span>
                  </div>
                  <div className="h-8 w-px bg-background-200"></div>
                  <div className="flex items-center gap-1 text-sm text-primary-500 font-semibold">
                    <i className="ri-fire-line"></i>
                    <span>{article.heat}热度</span>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-[11px] font-medium bg-background-100 text-foreground-600 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <div
                className="prose prose-lg max-w-none text-foreground-800
                  prose-p:leading-relaxed prose-p:mb-5 prose-p:text-[15px]
                  prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                  prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </div>
        </section>

        <section className="py-16 bg-background-950">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-5xl mx-auto flex flex-col lg:flex-row">
              <div className="lg:w-[38%] -ml-4 lg:-ml-12 my-4 lg:my-8">
                <div className="h-[300px] lg:h-[420px] rounded-[30px] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Anonymous%20figure%20in%20dark%20room%20holding%20phone%20with%20glowing%20screen%2C%20half%20face%20hidden%20in%20shadow%2C%20dramatic%20chiaroscuro%20portrait%2C%20cinematic%20noir%20style%2C%20deep%20shadows%2C%20neutral%20gray%20background%2C%20mysterious%20whistleblower%20mood%2C%20clean%20composition&width=600&height=800&seq=testimonial-article-911&orientation=portrait"
                    alt="爆料者"
                    title="匿名爆料者"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="lg:w-[62%] flex flex-col justify-center px-6 lg:px-10 py-8 lg:py-12">
                <div className="text-6xl text-accent-300/40 leading-none mb-4 font-serif">&ldquo;</div>
                <blockquote className="text-lg md:text-xl text-background-50 leading-relaxed">
                  本爆料内容由圈内知情人士独家提供，911爆料网已对内容进行了多方核实。爆料人身份信息严格保密，请各位理性吃瓜。
                </blockquote>
                <div className="mt-6">
                  <p className="text-sm font-semibold text-background-50">{article.author}</p>
                  <p className="text-xs text-background-50/50 mt-0.5">{article.authorTitle}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <h2 className="text-2xl md:text-3xl font-black text-foreground-950 mb-8">
                相关<span className="text-primary-500">爆料</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-black text-foreground-950 mb-4">
                更多<span className="text-primary-500">猛料</span>推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                {articles.filter(a => a.id !== article.id).slice(0, 4).map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    className="flex gap-3 p-4 bg-background-100 rounded-xl hover:bg-background-200 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-20 h-16 shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.imageAlt}
                        title={a.title}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug">{a.title}</h3>
                      <span className="text-[11px] text-foreground-500 mt-1.5">{a.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}